import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import apiClient from '../../../services/apiService';
import { DASH_TOOLTIP } from './tooltipStyle';

// 'YYYY-MM' -> 'Sep' (or 'Sep 2025' when withYear).
const monthLabel = (m, withYear = false) => {
  if (typeof m !== 'string' || !m.includes('-')) return m;
  const [y, mm] = m.split('-');
  const d = new Date(Number(y), Number(mm) - 1, 1);
  if (Number.isNaN(d.getTime())) return m;
  return d.toLocaleString('en-US', withYear ? { month: 'short', year: 'numeric' } : { month: 'short' });
};

// A single month that dwarfs the rest (>5x the next-highest) is a one-time
// bulk-import artifact, not organic onboarding. Drop that point from the line
// (set to null so the wave connects across it) — data itself is untouched.
const OUTLIER_RATIO = 5;
const processTrend = (data) => {
  const vals = data.map((d) => Number(d.registered) || 0);
  if (vals.length < 3) return { cleaned: data, excludedMonth: null };
  const max = Math.max(...vals);
  const maxIdx = vals.indexOf(max);
  const second = [...vals].sort((a, b) => b - a)[1] ?? 0;
  if (max <= OUTLIER_RATIO * Math.max(second, 1)) return { cleaned: data, excludedMonth: null };
  const cleaned = data.map((d, i) => (i === maxIdx ? { ...d, registered: null } : d));
  return { cleaned, excludedMonth: data[maxIdx]?.month ?? null };
};

// Fixed gap-of-10 ticks scaled to the (outlier-excluded) month range.
const yAxisTicks = (cleaned) => {
  const yVals = cleaned.map((d) => d.registered).filter((v) => v != null).map(Number);
  const scaleMax = Math.max(10, ...(yVals.length ? yVals : [10]));
  const niceMax = Math.ceil(scaleMax / 10) * 10;
  const ticks = [];
  for (let t = 0; t <= niceMax; t += 10) ticks.push(t);
  return { ticks, niceMax };
};

const OnboardingTrendsChart = () => {
  const [data, setData] = useState([]);
  const [, setLoading] = useState(true);
  const [, setError] = useState(null);

  useEffect(() => {
    const fetchOnboardingTrends = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/dashboard/onboarding-trends', { params: { months: 12 } });
        setData(response.data.data || response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching onboarding trends:', err);
        setError('Failed to load onboarding trends');
        setData([
          { month: '2026-01', registered: 12, approved: 10 },
          { month: '2026-02', registered: 18, approved: 15 },
          { month: '2026-03', registered: 25, approved: 22 },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchOnboardingTrends();
  }, []);

  const { cleaned, excludedMonth } = processTrend(data);
  const { ticks, niceMax } = yAxisTicks(cleaned);

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between mb-2 pb-2 border-b border-dash-border flex-shrink-0">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-dash-foreground">Onboarding Trend</h3>
        <span className="text-[11px] text-dash-muted-foreground">last 12 months</span>
      </div>
      <div style={{ height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={cleaned} margin={{ top: 8, right: 12, bottom: 0, left: -8 }}>
            <defs>
              <linearGradient id="trendFade" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="var(--dash-chart-1)" stopOpacity={0.35} />
                <stop offset="70%" stopColor="var(--dash-chart-1)" stopOpacity={1} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="var(--dash-border)" vertical={false} />
            <XAxis
              dataKey="month"
              tickFormatter={(m) => monthLabel(m)}
              tickLine={false}
              axisLine={{ stroke: 'var(--dash-border)' }}
              tick={{ fontSize: 11, fill: 'var(--dash-muted-foreground)' }}
            />
            <YAxis
              domain={[0, niceMax]}
              ticks={ticks}
              tickLine={false}
              axisLine={false}
              width={34}
              tick={{ fontSize: 11, fill: 'var(--dash-muted-foreground)' }}
            />
            <Tooltip
              labelFormatter={(m) => monthLabel(m, true)}
              contentStyle={DASH_TOOLTIP.contentStyle}
              labelStyle={DASH_TOOLTIP.labelStyle}
              itemStyle={DASH_TOOLTIP.itemStyle}
            />
            <Line
              type="monotone"
              dataKey="registered"
              connectNulls
              stroke="url(#trendFade)"
              strokeWidth={2}
              dot={{ r: 2.5, fill: 'var(--dash-card)', stroke: 'var(--dash-chart-1)', strokeWidth: 1.5 }}
              activeDot={{ r: 4 }}
              name="Registered"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {excludedMonth ? (
        <p className="mt-1 text-[10px] text-dash-muted-foreground">
          Excludes one-time bulk import ({monthLabel(excludedMonth, true)}).
        </p>
      ) : null}
    </div>
  );
};

export default OnboardingTrendsChart;
